import React, { useState, useRef, useEffect } from 'react'; // Added useEffect
import { useTranslation } from 'react-i18next';
import { Send, Paperclip, Smile, XCircle, FileText, Video, Volume2, Image as ImageIcon } from 'lucide-react';
import { Message } from '../../types/chat'; // Import Message type
import Picker, { EmojiClickData, Theme } from 'emoji-picker-react'; // Added Picker and types

interface MessageInputProps {
  onSendMessage: (channelId: string, content: string, mediaFile?: File, quotedMsgId?: string | null) => void;
  disabled?: boolean;
  quotedMsgId?: string | null;
  channelId: string;
  setQuotedMsgId: (id: string | null) => void;
  // quotedMessageContent?: string | null; // Removed
  quotedMessageDetails?: Message | null; // Added for full message details
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  quotedMsgId = null,
  channelId,
  setQuotedMsgId,
  // quotedMessageContent, // Removed
  quotedMessageDetails, // Destructure new prop
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // Added
  const emojiPickerRef = useRef<HTMLDivElement>(null); // Added

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    } else {
      setSelectedFile(null);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const performSubmit = () => {
    if ((message.trim() || selectedFile) && !disabled) {
      onSendMessage(channelId, message.trim(), selectedFile, quotedMsgId);
      setMessage('');
      setSelectedFile(null);
      setShowEmojiPicker(false); // Close picker on send
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSubmit();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      performSubmit();
    }
  };

  // Helper function to render quoted message preview
  const renderQuotedMessagePreview = (messageToPreview: Message) => {
    switch (messageToPreview.messageType) {
      case 'text':
        return <p className="truncate">{messageToPreview.content}</p>;
      case 'image':
        // Assuming message.content could be a URL or filename
        const isImageUrl = messageToPreview.content.match(/\.(jpeg|jpg|gif|png)$/) != null || messageToPreview.content.startsWith('http');
        if (isImageUrl) {
          return (
            <div className="flex items-center">
              <ImageIcon size={16} className="mr-1 flex-shrink-0" />
              <img src={messageToPreview.content} alt="Quoted image" className="max-h-10 max-w-xs object-contain truncate" />
            </div>
          );
        }
        return (
          <div className="flex items-center">
            <ImageIcon size={16} className="mr-1 flex-shrink-0" />
            <span className="truncate">{messageToPreview.content || t('image')}</span>
          </div>
        );
      case 'video':
        return (
          <div className="flex items-center">
            <Video size={16} className="mr-1 flex-shrink-0" />
            <span className="truncate">{messageToPreview.content || t('video')}</span>
          </div>
        );
      case 'audio':
      case 'ptt':
        return (
          <div className="flex items-center">
            <Volume2 size={16} className="mr-1 flex-shrink-0" />
            <span className="truncate">{messageToPreview.content || t('audio')}</span>
          </div>
        );
      case 'document':
        return (
          <div className="flex items-center">
            <FileText size={16} className="mr-1 flex-shrink-0" />
            <span className="truncate">{messageToPreview.content || t('document')}</span>
          </div>
        );
      default:
        return <p className="truncate">{messageToPreview.content || quotedMsgId}</p>;
    }
  };

  const onEmojiClick = (emojiData: EmojiClickData) => { // Removed 'event' as it's not used
    setMessage(prevMessage => prevMessage + emojiData.emoji);
  };

  // Handle clicking outside the emoji picker to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        // Also check if the click was on the emoji button itself
        const emojiButton = document.getElementById("emoji-button"); // Ensure your emoji button has this ID or a ref
        if (emojiButton && emojiButton.contains(event.target as Node)) {
          return; // Do not close if emoji button was clicked
        }
        setShowEmojiPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [emojiPickerRef]);

  if (disabled) {
    return (
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="bg-gray-100 rounded-lg px-4 py-3 text-center text-red-500"> {/* Optional: change text color */}
          {t('cannotSendMessages')}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white border-t border-gray-200 relative"> {/* Added relative for picker positioning */}
      {showEmojiPicker && (
        <div ref={emojiPickerRef} className="absolute bottom-full right-0 mb-2 z-20 sm:right-auto sm:left-12"> {/* Adjusted left positioning for sm screens */}
          <Picker
            onEmojiClick={onEmojiClick}
            autoFocusSearch={false}
            theme={Theme.AUTO}
            lazyLoadEmojis={true}
            height={350}
            width={300}
            // categories={[ // Optional: customize categories
            //   { category: 'smileys_people', name: 'Smileys & People' },
            //   { category: 'animals_nature', name: 'Animals & Nature' },
            //   { category: 'food_drink', name: 'Food & Drink' },
            // ]}
          />
        </div>
      )}
      {/* Display Quoted Message UI */}
      {quotedMsgId && quotedMessageDetails && setQuotedMsgId && (
        <div className="mb-2 p-2 bg-gray-100 rounded-md text-sm text-gray-700 flex justify-between items-center">
          <div className="truncate">
            <p className="font-semibold">{t('replyingTo')}</p>
            {/* Use the helper function to render preview */}
            {renderQuotedMessagePreview(quotedMessageDetails)}
          </div>
          <button
            type="button"
            onClick={() => setQuotedMsgId(null)}
            className="ml-2 text-gray-500 hover:text-gray-700"
            aria-label={t('clearQuote')}
          >
            <XCircle size={16} />
          </button>
        </div>
      )}

      {/* Display selected file and clear button */}
      {selectedFile && (
        <div className="mb-2 flex items-center text-sm text-gray-700 bg-gray-100 p-2 rounded-md">
          <Paperclip size={16} className="mr-2 flex-shrink-0" />
          <span className="truncate flex-grow min-w-0">{selectedFile.name}</span>
          <button
            type="button"
            onClick={() => setSelectedFile(null)}
            className="ml-2 text-red-500 hover:text-red-700 flex-shrink-0"
            aria-label={t('removeFile')}
          >
            <XCircle size={16} />
          </button>
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex items-end space-x-2">
        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          // Optional: Add accept attribute for specific file types
          // accept="image/*,application/pdf,.doc,.docx"
        />

        {/* Attachment Button */}
        <button
          type="button"
          onClick={triggerFileInput}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          disabled={disabled}
        >
          <Paperclip className="w-5 h-5" />
        </button>

        {/* Message Input */}
        <div className="flex-1 relative">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('typeAMessage')}
            className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none max-h-32 min-h-[48px]" // Adjusted padding for emoji button
            rows={1}
            disabled={disabled}
          />
          
          {/* Emoji Button */}
          <button
            id="emoji-button" // Added ID for outside click detection
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 rounded-full transition-colors"
            disabled={disabled}
            onClick={() => setShowEmojiPicker(!showEmojiPicker)} // Toggle picker
            title={t('addEmoji')}
          >
            <Smile className="w-5 h-5" />
          </button>
        </div>

        {/* Send Button */}
        <button
          type="submit"
          disabled={!(message.trim() || selectedFile) || disabled}
          className={`p-3 rounded-full transition-colors ${
            (message.trim() || selectedFile) && !disabled
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
};

export default MessageInput;